import { Box, Button, CircularProgress, Stack, Switch, Typography } from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { PinnacleTextField } from "../../../../../components/PinnacleTextField";
import { PREBOARD_ACTIVATE_CODE_IMG_URL } from "../../../../../utils/constants";
import { activateExamCode } from "../../../../../api/services/code";
import { useState } from "react";
import { handleApiError } from "../../../../../utils/errorHandler";
import { pushNotification } from "../../../../../components/PinnacleSnackbarProvider/PinnacleSnackbarProvider";
import { NotificationTypes } from "../../../../../components/PinnacleSnackbarProvider/PinnacleSnackbarProvider.types";

interface ActivateExamCodeProps {
    onSuccessActivateCode?: () => void;
    onClose: () => void;
}

const ActivateExamCode: React.FC<ActivateExamCodeProps> = ({ onSuccessActivateCode, onClose }) => {
    const [activationStatus, setActivationStatus] = useState<string>('');
    const [code, setCode] = useState<string>('');
    const [formDisabled, setFormDisabled] = useState<boolean>(false);
    const [examCodeTouched, setExamCodeTouched] = useState<boolean>(false);
    const isExamCodeValid = code.length > 0;

    const handleSubmitActivateCode = async (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        try {
            setFormDisabled(true);
            const activateCode = await activateExamCode({ code });
            pushNotification({
                details: activateCode.message,
                type: NotificationTypes.Success,
            });
            onSuccessActivateCode?.();
            setActivationStatus('success');
        } catch (error) {
            setActivationStatus('error');
        } finally {
            setFormDisabled(false);
        }
    };

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Typography variant="body2" sx={{ fontWeight: 400, color: '#5840BA' }}>Exam Code:</Typography>
            <form onSubmit={handleSubmitActivateCode}>
                <PinnacleTextField
                    error={examCodeTouched && (!isExamCodeValid)}
                    color={
                        activationStatus === 'success'
                            ? 'success'
                            : activationStatus === 'error'
                                ? 'error'
                                : 'primary'
                    }
                    focused={activationStatus === 'success' || activationStatus === 'error'}
                    id="code"
                    placeholder="Enter the exam code here"
                    variant="outlined"
                    onChange={(e) => setCode(e.target.value)}
                    onBlur={() => setExamCodeTouched(true)}
                    value={code}
                    disabled={formDisabled}
                    slotProps={{
                        input: {
                            endAdornment: (
                                <Typography
                                    variant="body2"
                                    sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        gap: 0.5,
                                        color: activationStatus === 'success' ? "#028A4F" : "#5840BA",
                                        fontWeight: 500,
                                        cursor: "pointer",
                                    }}
                                    onClick={(e: any) => handleSubmitActivateCode(e)}
                                >
                                    {formDisabled ? (
                                        <CircularProgress size={24} sx={{ color: "#90caf9" }} />
                                    ) : activationStatus === 'success' ? (
                                        <>
                                            ACTIVATED
                                            <CheckCircleIcon sx={{ fontSize: 18 }} />
                                        </>
                                    ) : (
                                        "ACTIVATE"
                                    )}
                                </Typography>
                            ),
                        }
                    }}
                />
            </form>

            {activationStatus === 'success' ? (
                <>
                    <Typography variant="body1" sx={{ fontWeight: 400, lineHeight: 1.5 }}>
                        <span style={{ color: '#028A4F' }}>Success! examin code is valid.</span>
                        <br />
                        <span style={{ color: '#373737' }}>You may close this window and proceed with the exam.</span>
                    </Typography>
                </>
            ) : activationStatus === 'error' ? (
                <Typography variant="body1" sx={{ color: '#FA3D3D', fontWeight: 400, lineHeight: 1.5 }}>
                    <span>You have entered an invalid exam code.</span>
                    <br />
                    <span>Please try again.</span>
                </Typography>
            ) : (
                <></>
            )}
        </Box>
    );
};

export default ActivateExamCode;